import { useEffect, useMemo, useState } from "react";
import { Button, Form, Header, Icon, Label, Message, Segment } from "semantic-ui-react";

function naturalSort(a, b) {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

export default function FloorPriorityTab({ guestHouses, rooms, floorPriorities, onAdd, onUpdate, onDelete }) {
  const [selectedGhId, setSelectedGhId] = useState(guestHouses[0]?.id || "");
  const [rankedFloors, setRankedFloors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const selectedGh = useMemo(
    () => guestHouses.find((g) => g.id === selectedGhId) || guestHouses[0],
    [guestHouses, selectedGhId],
  );

  useEffect(() => {
    if (!selectedGhId && guestHouses[0]) setSelectedGhId(guestHouses[0].id);
  }, [guestHouses, selectedGhId]);

  const savedRows = selectedGh ? floorPriorities?.[selectedGh.id] || [] : [];
  const savedRowsKey = JSON.stringify(savedRows.map((r) => [r.id, r.floor, r.priority]));

  const savedOrder = useMemo(
    () => [...savedRows].sort((a, b) => a.priority - b.priority).map((r) => r.floor),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [savedRowsKey],
  );

  /* Re-sync local working order whenever the guest house changes or the
     saved data changes (initial load, or right after this tab's own save). */
  useEffect(() => {
    setRankedFloors(savedOrder);
    setError("");
    setSaved(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGh?.id, savedRowsKey]);

  const knownFloors = useMemo(() => {
    if (!selectedGh) return [];
    const fromRooms = (rooms || [])
      .filter((r) => r.guestHouseId === selectedGh.id && r.floor)
      .map((r) => r.floor);
    const fromPriorities = savedRows.map((r) => r.floor);
    return Array.from(new Set([...fromRooms, ...fromPriorities])).sort(naturalSort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, selectedGh, savedRowsKey]);

  const unrankedFloors = knownFloors.filter((f) => !rankedFloors.includes(f));
  const dirty = JSON.stringify(rankedFloors) !== JSON.stringify(savedOrder);

  const moveUp = (idx) => {
    if (idx === 0) return;
    setRankedFloors((prev) => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
    setSaved(false);
  };

  const moveDown = (idx) => {
    setRankedFloors((prev) => {
      if (idx === prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
    setSaved(false);
  };

  const unrank = (floor) => {
    setRankedFloors((prev) => prev.filter((f) => f !== floor));
    setSaved(false);
  };

  const rank = (floor) => {
    setRankedFloors((prev) => [...prev, floor]);
    setSaved(false);
  };

  const resetOrder = () => {
    setRankedFloors(savedOrder);
    setError("");
  };

  const handleSave = async () => {
    if (!selectedGh) return;
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const rowByFloor = new Map(savedRows.map((r) => [r.floor, r]));
      const keepFloors = new Set(rankedFloors);
      const ops = [];
      rankedFloors.forEach((floor, i) => {
        const priority = i + 1;
        const existing = rowByFloor.get(floor);
        if (!existing) {
          ops.push(onAdd(selectedGh.id, { floor, priority }));
        } else if (existing.priority !== priority) {
          ops.push(onUpdate(selectedGh.id, existing.id, { floor, priority }));
        }
      });
      savedRows.forEach((row) => {
        if (!keepFloors.has(row.floor)) {
          ops.push(onDelete(selectedGh.id, row.id));
        }
      });
      await Promise.all(ops);
      setSaved(true);
    } catch (e) {
      setError(e.message || "Failed to save floor priority.");
    } finally {
      setSaving(false);
    }
  };

  if (!selectedGh) {
    return <Segment basic>No guest house available.</Segment>;
  }

  return (
    <div>
      <Segment>
        {guestHouses.length > 1 && (
          <Form style={{ marginBottom: 16 }}>
            <Form.Select
              label="Guest House"
              options={guestHouses.map((g) => ({ key: g.id, text: g.name, value: g.id }))}
              value={selectedGh.id}
              onChange={(_, data) => setSelectedGhId(data.value)}
            />
          </Form>
        )}

        <Header as="h5">Fill Order</Header>
        <p style={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}>
          Auto-booking fills rooms from the first floor below, then the next, and so on.
        </p>

        {rankedFloors.length === 0 ? (
          <p style={{ fontStyle: "italic", color: "rgba(0,0,0,0.5)" }}>
            No floors ranked yet — add floors from the list below to set a fill order.
          </p>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4 }}>
            {rankedFloors.map((floor, idx) => (
              <div key={floor} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {idx > 0 && <Icon name="angle right" color="grey" />}
                <Label size="large" color="blue">
                  Floor {floor}
                  <Icon
                    name="angle left"
                    title="Move earlier"
                    style={{
                      marginLeft: 10,
                      cursor: idx === 0 ? "default" : "pointer",
                      opacity: idx === 0 ? 0.3 : 1,
                    }}
                    onClick={() => moveUp(idx)}
                  />
                  <Icon
                    name="angle right"
                    title="Move later"
                    style={{
                      cursor: idx === rankedFloors.length - 1 ? "default" : "pointer",
                      opacity: idx === rankedFloors.length - 1 ? 0.3 : 1,
                    }}
                    onClick={() => moveDown(idx)}
                  />
                  <Icon
                    name="delete"
                    title="Remove from ranking"
                    style={{ cursor: "pointer" }}
                    onClick={() => unrank(floor)}
                  />
                </Label>
              </div>
            ))}
          </div>
        )}

        <Header as="h5" style={{ marginTop: 28 }}>
          Other Floors{" "}
          <span style={{ fontWeight: 400, fontSize: 12, color: "rgba(0,0,0,0.5)" }}>
            (fill last, random order)
          </span>
        </Header>
        {unrankedFloors.length === 0 ? (
          <p style={{ fontStyle: "italic", color: "rgba(0,0,0,0.5)" }}>
            {knownFloors.length === 0
              ? "No floors found for this guest house yet — add rooms first."
              : "All floors are ranked."}
          </p>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {unrankedFloors.map((floor) => (
              <Label key={floor} as="a" basic onClick={() => rank(floor)}>
                <Icon name="plus" />
                Floor {floor}
              </Label>
            ))}
          </div>
        )}

        {error && <Message negative content={error} style={{ marginTop: 16 }} />}
        {saved && !dirty && <Message positive content="Floor priority saved." style={{ marginTop: 16 }} />}
      </Segment>
      <Segment basic textAlign="right">
        <Button basic disabled={!dirty || saving} onClick={resetOrder}>
          Discard Changes
        </Button>
        <Button primary loading={saving} disabled={saving || !dirty} onClick={handleSave}>
          <Icon name="save" />
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </Segment>
    </div>
  );
}
